// O BALÃO FLUTUANTE do assistente — a peça, sozinha.
//
// Nasceu em `LeitorUi.tsx` em 03/09/2026, a pedido de Victor, e saiu de lá em
// 14/09 quando a Central IBS-CBS passou a precisar da mesma coisa. Ela não tem
// NADA do Leitor dentro: todas as sete propriedades são dado (título, apoio,
// selo, rótulo, o nó que rola, as falas e o rodapé). Copiá-la para a Central
// duplicaria três decisões MEDIDAS — a altura que desconta `--cf-topo`, o
// Escape que devolve o foco pelo efeito (e não no handler) e o `z-30` abaixo do
// Topo —, e a segunda cópia divergiria da primeira em silêncio.
//
// `LeitorUi.tsx` continua exportando o nome, por reexportação: quem importa de
// lá não precisou mudar, e as sabotagens de tela do Leitor continuam apontando
// para a decisão, que agora mora aqui.
import { useEffect, useRef, type ReactNode, type RefObject } from "react";

// ===========================================================================
// O BALÃO FLUTUANTE do assistente
// ===========================================================================
// Pedido de Victor em 03/09/2026: a conversa sobre o processo deixa de ser a
// última seção da página e passa a ser um balão no canto inferior direito.
//
// POR QUE ISSO É MELHOR, e não só diferente: a pergunta que o perito faz nasce
// DO QUE ELE ESTÁ LENDO — "esse valor da página 34 aparece em outro lugar?".
// Com a conversa no fim de uma página de quatro abas, perguntar custava rolar
// até o fim, e a resposta chegava longe do trecho que a motivou. O balão fica
// à mão em qualquer aba, e a leitura continua atrás dele.
//
// ── Disclosure, e NÃO `<dialog>` ──────────────────────────────────────────
// A tentação é usar o `<dialog>` nativo, que o hub já usa em quatro lugares.
// Aqui ele está errado: `showModal()` torna inerte o resto da página, e o
// ponto desta peça é justamente consultar o assistente ENQUANTO se lê o
// relatório. Então é o padrão de disclosure — botão com `aria-expanded` e
// `aria-controls`, painel com `role="dialog"` e SEM `aria-modal`.
//
// Consequência: nada aqui tranca o foco, de propósito. `Tab` sai do balão para
// a página, que é o comportamento certo para um painel não-modal.
export function BalaoDoAssistente({
  aberto,
  aoTrocar,
  titulo,
  apoio,
  selo,
  rotuloFechado,
  corpoRef,
  children,
  rodape,
}: {
  aberto: boolean;
  aoTrocar: (aberto: boolean) => void;
  titulo: string;
  apoio?: string;
  /** Quantas falas há, para o botão fechado dizer que existe conversa. */
  selo?: number;
  rotuloFechado: string;
  /**
   * O nó que ROLA, entregue a quem escreve as falas — quem sabe que chegou
   * resposta é ele, e a rolagem tem de ser por `scrollTop` DESTE nó (rolar por
   * `scrollIntoView` levaria a página junto).
   */
  corpoRef?: RefObject<HTMLDivElement | null>;
  /** O corpo que ROLA — as falas. */
  children: ReactNode;
  /** O que fica PRESO embaixo: o formulário. */
  rodape?: ReactNode;
}) {
  const botao = useRef<HTMLButtonElement>(null);
  /**
   * "Fechei por gesto, devolve o foco ao botão."
   *
   * Precisa ser um recado para DEPOIS, e não um `focus()` na hora, por uma razão
   * que a prova de tela pegou em 03/09: o botão fechado só existe no DOM quando
   * `aberto` é falso, então no instante em que Escape (ou o X) roda, o
   * `botao.current` ainda é **null** — o React ainda não remontou o botão. A
   * primeira versão chamava `botao.current?.focus()` ali e o foco caía no
   * `<body>`, de onde quem usa teclado recomeça do alto da página.
   */
  const devolverFoco = useRef(false);

  useEffect(() => {
    if (aberto || !devolverFoco.current) return;
    devolverFoco.current = false;
    botao.current?.focus();
  }, [aberto]);

  // `Escape` fecha e devolve o foco ao botão. O listener é no `document` e não
  // no painel porque o perito pode ter clicado no relatório atrás — e ali o
  // foco não está dentro do balão, mas a expectativa de que Escape feche
  // continua.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      devolverFoco.current = true;
      aoTrocar(false);
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, aoTrocar]);

  return (
    // `z-30` fica ABAIXO do Topo (`z-40`), que é a navegação e continua
    // alcançável; e a altura desconta `--cf-topo` para o painel nunca encostar
    // nele. Cravar a altura do Topo aqui seria o acoplamento que a variável
    // existe para evitar (a cicatriz do `rolarPara`).
    <div className="fixed right-3 bottom-3 z-30 sm:right-6 sm:bottom-6">
      {!aberto && (
        <button
          ref={botao}
          type="button"
          onClick={() => aoTrocar(true)}
          // `aria-expanded` cravado em false porque este botão só existe
          // FECHADO — quem está aberto tem o X do cabeçalho. E o `aria-controls`
          // aponta para um id que só passa a existir depois de abrir, que é o
          // padrão de disclosure com conteúdo removido do DOM: quem diz o estado
          // é o `aria-expanded`, e o `aria-controls` liga os dois quando o
          // painel está lá. Manter o painel montado e escondido resolveria a
          // formalidade e custaria o foco — um campo de texto oculto continua
          // tabulável se alguém esquecer o `inert`.
          aria-expanded={false}
          aria-controls="balao-do-assistente"
          className="pill-cta flex items-center gap-2 shadow-[0_10px_30px_-10px_rgba(5,13,28,0.55)]"
        >
          <BolhasDeConversa />
          <span>{rotuloFechado}</span>
          {selo !== undefined && selo > 0 && (
            <span className="rounded-full bg-navy-ink/15 px-2 py-0.5 text-xs font-bold tabular-nums">
              {selo}
            </span>
          )}
        </button>
      )}

      {aberto && (
        <section
          id="balao-do-assistente"
          role="dialog"
          aria-labelledby="titulo-do-balao"
          // A largura respeita a tela pequena, e a altura desconta o Topo: num
          // celular baixo o formulário TEM de continuar alcançável, que é o
          // mesmo motivo de o modal do Mapa de Concursos ter o `max-h` no
          // invólucro e a rolagem só na seção do meio.
          className="surface flex h-[min(33rem,calc(100dvh-var(--cf-topo)-2.5rem))] w-[min(27rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-cf shadow-[0_24px_60px_-20px_rgba(5,13,28,0.6)]"
        >
          {/* O cabeçalho é navy: é território, e dá ao balão a borda visível
              que um painel flutuante precisa ter sobre conteúdo claro. Dentro
              dele, cor de marca — `navy-mute` para o apoio, nunca `cinza`. */}
          <header className="painel-navy flex shrink-0 items-start gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <h2 id="titulo-do-balao" className="font-display text-base font-bold text-white">
                {titulo}
              </h2>
              {apoio && <p className="mt-0.5 text-xs text-navy-mute">{apoio}</p>}
            </div>
            <button
              type="button"
              onClick={() => {
                // O recado, não o `focus()`: o botão que vai receber o foco só
                // volta ao DOM depois deste render.
                devolverFoco.current = true;
                aoTrocar(false);
              }}
              aria-label="Fechar o assistente"
              className="-mr-1 shrink-0 rounded-full p-1.5 text-navy-mute transition hover:bg-white/10 hover:text-white"
            >
              <svg width="1.15em" height="1.15em" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M5.5 5.5l9 9m0-9l-9 9"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>

          {/* A rolagem é DAQUI, e o `scrollTop` de quem escreve mira este nó —
              `scrollIntoView` levaria a PÁGINA junto, e a página é o relatório
              que o perito está lendo. */}
          <div
            ref={corpoRef}
            data-corpo-do-balao
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
          >
            {children}
          </div>

          {rodape && (
            <div className="shrink-0 border-t border-borda bg-superficie-2 px-4 py-3">{rodape}</div>
          )}
        </section>
      )}
    </div>
  );
}

/** O ícone do botão fechado. Dimensão INTRÍNSECA, para nenhum `size-*` sumido
 *  deixá-lo esticar — a cicatriz das 34 setas gigantes do IR Pro. */
function BolhasDeConversa() {
  return (
    <svg width="1.15em" height="1.15em" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 8.2c0-2.3 2.4-4.2 5.4-4.2s5.4 1.9 5.4 4.2-2.4 4.2-5.4 4.2c-.6 0-1.2-.1-1.7-.2L4 13.4l.6-2.1C3.6 10.5 3 9.4 3 8.2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M11.6 13.6c.5.2 1.1.3 1.7.3.4 0 .8 0 1.2-.1l2.5.9-.6-2c.9-.7 1.5-1.7 1.5-2.8 0-1.5-1-2.8-2.5-3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
